import * as tf from '@tensorflow/tfjs';
import type { VAEConfig } from '../types.ts';
import { vaeLoss } from './loss.ts';

export class VAEModel {
  encoder: tf.LayersModel;
  decoder: tf.LayersModel;
  optimizer: tf.Optimizer;
  config: VAEConfig;
  imageSize = 784;
  inputShape: [number, number] = [28, 28];

  constructor(config: VAEConfig) {
    this.config = config;
    this.encoder = this.buildEncoder();
    this.decoder = this.buildDecoder();
    this.optimizer = tf.train.adam(config.learningRate);
  }

  private buildEncoder(): tf.LayersModel {
    const { mode, latentDim } = this.config;
    const inputs = tf.input({ shape: [28, 28, 1] });
    let x = inputs;

    if (mode === 'simple') {
      x = tf.layers.flatten().apply(x) as tf.SymbolicTensor;
      x = tf.layers.dense({ units: this.config.simpleModeHiddenUnits, activation: 'relu' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.dense({ units: this.config.simpleModeHiddenUnits / 2, activation: 'relu' }).apply(x) as tf.SymbolicTensor;
    } else {
      x = tf.layers.conv2d({ filters: 32, kernelSize: 3, activation: 'relu', padding: 'same' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.maxPooling2d({ poolSize: 2, strides: 2 }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.conv2d({ filters: 64, kernelSize: 3, activation: 'relu', padding: 'same' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.maxPooling2d({ poolSize: 2, strides: 2 }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.flatten().apply(x) as tf.SymbolicTensor;
      x = tf.layers.dense({ units: 128, activation: 'relu' }).apply(x) as tf.SymbolicTensor;
    }

    const mu = tf.layers.dense({ units: latentDim, name: 'mu' }).apply(x) as tf.SymbolicTensor;
    const logVar = tf.layers.dense({ units: latentDim, name: 'log_var' }).apply(x) as tf.SymbolicTensor;

    const model = tf.model({ inputs, outputs: [mu, logVar] });
    return model;
  }

  private buildDecoder(): tf.LayersModel {
    const { mode, latentDim } = this.config;
    const inputs = tf.input({ shape: [latentDim] });
    let x = inputs;

    if (mode === 'simple') {
      x = tf.layers.dense({ units: this.config.simpleModeHiddenUnits / 2, activation: 'relu' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.dense({ units: this.config.simpleModeHiddenUnits, activation: 'relu' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.dense({ units: this.imageSize, activation: 'sigmoid' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.reshape({ targetShape: [28, 28, 1] }).apply(x) as tf.SymbolicTensor;
    } else {
      x = tf.layers.dense({ units: 128, activation: 'relu' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.dense({ units: 7 * 7 * 64, activation: 'relu' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.reshape({ targetShape: [7, 7, 64] }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.conv2dTranspose({ filters: 64, kernelSize: 3, strides: 2, padding: 'same', activation: 'relu' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.conv2dTranspose({ filters: 32, kernelSize: 3, strides: 2, padding: 'same', activation: 'relu' }).apply(x) as tf.SymbolicTensor;
      x = tf.layers.conv2d({ filters: 1, kernelSize: 3, padding: 'same', activation: 'sigmoid' }).apply(x) as tf.SymbolicTensor;
    }

    const model = tf.model({ inputs, outputs: x });
    return model;
  }

  sample(mu: tf.Tensor, logVar: tf.Tensor): tf.Tensor {
    const shape = mu.shape;
    const epsilon = tf.randomNormal(shape);
    const std = tf.exp(tf.mul(logVar, 0.5));
    return tf.add(mu, tf.mul(std, epsilon));
  }

  encode(images: tf.Tensor): [tf.Tensor, tf.Tensor] {
    return this.encoder.predict(images) as [tf.Tensor, tf.Tensor];
  }

  decode(z: tf.Tensor): tf.Tensor {
    return this.decoder.predict(z) as tf.Tensor;
  }

  forward(images: tf.Tensor): {
    reconstructed: tf.Tensor;
    mu: tf.Tensor;
    logVar: tf.Tensor;
    z: tf.Tensor;
  } {
    const [mu, logVar] = this.encode(images);
    const z = this.sample(mu, logVar);
    const reconstructed = this.decode(z);
    return { reconstructed, mu, logVar, z };
  }

  async trainStep(images: tf.Tensor4D): Promise<{
    loss: number;
    reconLoss: number;
    klLoss: number;
  }> {
    const { betaVaeWeight } = this.config;

    const { value, grads } = tf.variableGrads(() => {
      const { reconstructed, mu, logVar } = this.forward(images);
      const { loss } = vaeLoss(images, reconstructed, mu, logVar, betaVaeWeight);
      tf.dispose([reconstructed, mu, logVar]);
      return loss as tf.Scalar;
    });

    const loss = value.dataSync()[0];
    this.optimizer.applyGradients(grads);

    const [mu, logVar] = this.encode(images);
    const z = this.sample(mu, logVar);
    const recon = this.decode(z);
    const losses = vaeLoss(images, recon, mu, logVar, betaVaeWeight);
    const [reconVal, klVal] = await Promise.all([
      losses.reconLoss.data().then(d => d[0]),
      losses.klLoss.data().then(d => d[0]),
    ]);

    tf.dispose([value, ...Object.values(grads), recon, z, mu, logVar, losses.loss, losses.reconLoss, losses.klLoss]);

    return { loss, reconLoss: reconVal, klLoss: klVal };
  }

  async save(): Promise<void> {
    await this.encoder.save('indexeddb://vae-encoder');
    await this.decoder.save('indexeddb://vae-decoder');
  }

  async load(): Promise<void> {
    this.encoder = await tf.loadLayersModel('indexeddb://vae-encoder');
    this.decoder = await tf.loadLayersModel('indexeddb://vae-decoder');
  }

  dispose(): void {
    this.encoder.dispose();
    this.decoder.dispose();
  }
}
